import { useEffect, useRef, useState } from "react"
import { Alert, Box, Button, Stack, TextField, Typography } from "@mui/material"
import { Link, useNavigate } from "react-router-dom"
import { v4 as uuid } from "uuid"
import { Form } from "@core/components/form/form"
import {
  TelegramMessageEditor,
  type TelegramMessageEditorProps,
} from "@core/components/telegram/editor/telegramMessageEditor"
import {
  TelegramMessagePreview,
  type TelegramPreviewMedia,
} from "@core/components/telegram/preview/telegramMessagePreview"
import {
  collectSlotOccurrences,
  groupSlotOccurrences,
} from "@core/components/telegram/model/slots"
import {
  useVersionDraft,
  type VersionDraftOptions,
} from "../../hooks/useVersionDraft"
import { useMediaEditorAdapter } from "../media/mediaPicker"
import { FeatureErrors } from "../common/featureErrors"
import { PostBotEditorLayout } from "../common/postBotPageLayout"
import { readPostBotError } from "../../utils/apiErrors"
import {
  newTemplateVersionPath,
  readTemplateReturnContext,
  templatePath,
  templatesPath,
  templateVersionPath,
  VersionDraftNavigationGuard,
  type SavedTemplateReturnContext,
  type TemplateReturnContext,
} from "./templateNavigation"

export interface TemplateVersionEditorProps extends VersionDraftOptions {
  media?: TelegramPreviewMedia[]
  returnContext?: TemplateReturnContext
  onDirtyChange?: (dirty: boolean) => void
}

/** Session identity also keys Quill, picker state and last-good preview. */
export function TemplateVersionEditor(props: TemplateVersionEditorProps) {
  return <VersionAuthoringSession key={props.draftKey} {...props} />
}

function VersionAuthoringSession(props: TemplateVersionEditorProps) {
  const draft = useVersionDraft(props)
  const navigate = useNavigate()
  const serial = useRef(0)
  const [openOccurrence, setOpenOccurrence] =
    useState<TelegramMessageEditorProps["openOccurrence"]>()
  const returnContext = readTemplateReturnContext({
    sourceTemplateId: props.sourceVersion?.templateId ?? props.templateId,
    sourceTemplateVersionId: props.sourceVersion?.id,
    ...props.returnContext,
  })
  const media = useMediaEditorAdapter({
    value: draft.content,
    onChange: draft.setContent,
    media: props.media,
    readOnly: !draft.editable,
    listKey: "templateMedia",
    draftKey: props.draftKey,
  })
  const slots = groupSlotOccurrences(collectSlotOccurrences(draft.content)).map(
    ({ slotId, ...group }) => ({ id: slotId, ...group }),
  )
  const { onDirtyChange } = props
  useEffect(() => {
    onDirtyChange?.(draft.dirty)
  }, [draft.dirty, onDirtyChange])
  const returnState: SavedTemplateReturnContext | undefined =
    draft.savedVersionId
      ? {
          ...returnContext,
          sourceTemplateId: returnContext.sourceTemplateId ?? draft.templateId,
          sourceTemplateVersionId:
            returnContext.sourceTemplateVersionId ?? props.sourceVersion?.id,
          savedVersionId: draft.savedVersionId,
        }
      : undefined
  const empty =
    !draft.content.text &&
    !draft.content.mediaIds.length &&
    !draft.content.buttons.length
  const error =
    draft.error && typeof readPostBotError(draft.error).status !== "number"
      ? { data: { message: "Не удалось сохранить. Проверьте соединение." } }
      : draft.error
  return (
    <>
      <VersionDraftNavigationGuard dirty={draft.dirty} />
      <PostBotEditorLayout
        preview={
          <Box ref={media.previewRef}>
            {empty ? (
              <Typography
                color="text.secondary"
                sx={{ p: 3, textAlign: "center" }}
              >
                Здесь появится сообщение
              </Typography>
            ) : (
              <TelegramMessagePreview
                value={draft.content}
                lastGoodContent={draft.lastGoodContent}
                slots={slots}
                media={media.editorProps.media}
                onSelectOccurrence={(occurrence) =>
                  setOpenOccurrence({
                    id: ++serial.current,
                    occurrence,
                    content: draft.content,
                  })
                }
              />
            )}
          </Box>
        }
      >
        <Form
          onSubmit={() => draft.submit()}
          actions={[
            <Button
              key="save"
              type="submit"
              variant="contained"
              disabled={
                !draft.editable ||
                !!draft.uncertain ||
                !draft.validity.valid ||
                (!draft.templateId &&
                  (draft.name.length < 1 || draft.name.length > 200))
              }
            >
              Сохранить версию
            </Button>,
          ]}
        >
          {!props.sourceVersion && !props.templateId && (
            <TextField
              size="small"
              label="Название шаблона"
              value={draft.name}
              disabled={
                !draft.editable || !!draft.templateId || !!draft.uncertain
              }
              onChange={(event) => draft.setName(event.target.value)}
              inputProps={{ maxLength: 200 }}
            />
          )}
          <Typography variant="subtitle2">Сообщение</Typography>
          <TelegramMessageEditor
            {...media.editorProps}
            value={draft.content}
            onChange={draft.setContent}
            slots={slots}
            onCreateSlot={uuid}
            openOccurrence={openOccurrence}
            onBack={() =>
              navigate(
                draft.templateId
                  ? templatePath(draft.templateId)
                  : templatesPath,
                { state: returnContext },
              )
            }
            onRecover={
              draft.editable
                ? () =>
                    navigate(
                      draft.templateId
                        ? newTemplateVersionPath(draft.templateId)
                        : `${templatesPath}/new`,
                      { state: returnContext },
                    )
                : undefined
            }
          />
          {props.sourceVersion && (
            <Typography variant="caption" color="text.secondary">
              На основе версии {props.sourceVersion.number}. Публикации не
              переключаются.
            </Typography>
          )}
          <FeatureErrors error={error} />
          {draft.saving && <Typography role="status">Сохранение…</Typography>}
          {!!draft.validationError && (
            <Alert severity="error">{draft.validationError}</Alert>
          )}
          {draft.uncertain && (
            <Alert severity="warning">
              {draft.uncertain === "template"
                ? "Шаблон мог быть создан. Проверьте список перед повтором"
                : "Версия могла быть создана. Проверьте список перед повтором"}
              <Stack direction="row" useFlexGap flexWrap="wrap" spacing={1}>
                <Button
                  component={Link}
                  to={
                    draft.templateId
                      ? templatePath(draft.templateId)
                      : templatesPath
                  }
                  state={returnContext}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Открыть список
                </Button>
                <Button
                  disabled={!draft.editable}
                  onClick={() => void draft.retry()}
                >
                  Повторить сохранение
                </Button>
              </Stack>
            </Alert>
          )}
          {draft.savedVersionId && (
            <Alert severity="success">
              Новая версия сохранена.
              <Stack spacing={1}>
                <Button
                  component={Link}
                  to={templateVersionPath(draft.savedVersionId)}
                  state={returnContext}
                >
                  Открыть сохранённую версию
                </Button>
                {returnContext.returnToPublicationId && (
                  <Button
                    component={Link}
                    to={`/telegram/post-bot/publications/${encodeURIComponent(returnContext.returnToPublicationId)}`}
                    state={returnState}
                  >
                    Передать версию в черновик публикации
                  </Button>
                )}
              </Stack>
            </Alert>
          )}
        </Form>
      </PostBotEditorLayout>
      {media.picker}
    </>
  )
}
